import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, BuildingStorefrontIcon, CheckIcon } from '@heroicons/react/24/outline';

const FarmSearchDropdown = ({ farms, selectedFarmId, onSelect, label = "Farm" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter farms based on search term
  const filteredFarms = farms
    .filter(farm => farm.farm_name && farm.farm_name !== 'Unnamed Farm')
    .filter(farm => 
      farm.farm_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Get selected farm details
  const selectedFarm = selectedFarmId === 'all' 
    ? null 
    : farms.find(f => f.farm_id === parseInt(selectedFarmId) || f.farm_id === selectedFarmId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (farmId) => {
    onSelect(farmId);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      
      {/* Selected Farm Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-green-500 focus:border-green-500"
      >
        <div className="flex items-center gap-2 truncate">
          <BuildingStorefrontIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {selectedFarm 
              ? `${selectedFarm.farm_name} (${selectedFarm.farm_size || selectedFarm.farmsize || 0} hectares)`
              : `All Farms (${farms.filter(f => f.farm_name && f.farm_name !== 'Unnamed Farm').length})`
            }
          </span>
        </div>
        <svg className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search farms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* All Farms Option */}
          <div className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-100">
            All Farms ({filteredFarms.length})
          </div>

          {/* Farm List */}
          <div className="max-h-56 overflow-y-auto">
            {/* All Farms Option */}
            <div
              onClick={() => handleSelect('all')}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                selectedFarmId === 'all' ? 'bg-green-50' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">All Farms</div>
                  <div className="text-xs text-gray-500">{filteredFarms.length} farms</div>
                </div>
              </div>
              {selectedFarmId === 'all' && (
                <CheckIcon className="h-5 w-5 text-green-600" />
              )}
            </div>

            {/* Individual Farms */}
            {filteredFarms.map(farm => (
              <div
                key={farm.farm_id}
                onClick={() => handleSelect(farm.farm_id.toString())}
                className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                  selectedFarmId === farm.farm_id.toString() ? 'bg-green-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 truncate max-w-[200px]">{farm.farm_name}</div>
                    <div className="text-xs text-gray-500">{farm.farm_size || farm.farmsize || 0} hectares</div>
                  </div>
                </div>
                {selectedFarmId === farm.farm_id.toString() && (
                  <CheckIcon className="h-5 w-5 text-green-600" />
                )}
              </div>
            ))}

            {filteredFarms.length === 0 && (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                No farms found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmSearchDropdown;
